import React, { useState, useEffect, useRef, memo, useCallback } from 'react';
import { useTheme, useMediaQuery } from '@mui/material';
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import { openOrderWindow } from "../../store/actions/orders";
import { getProductType } from '../../utils/utilfunction';

import TableWrapper from './TableWrapper';
import ChartFrameModal from './ChartFrameModal';
import { useOptionChainData } from './useOptionChainData';
import OptionChainTableHeader from './OptionChainTableHeader';
import OptionChainTableBody from './OptionChainTableBody';
import MobileOptionDrawer from './MobileOptionDrawer';
import { useNavigate } from "react-router-dom";

const OptionTbl = memo(function OptionTbl({
  arrRows = [],
  callHeaders = [],
  putHeaders = [],
  underlyingToken,
  exchange,
  trading_symbol,
  underlyingLtp,
  selectedColumns
}) {
  const theme = useTheme();
  const { userSettings } = useSelector((state) => state.profile);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // ── Refs ──────────────────────────────────────────────────────────────
  const wrapperRef = useRef(null);
  const outerRef = useRef(null);
  const ceBodyClipRef = useRef(null);
  const peBodyClipRef = useRef(null);
  const ceBodyTableRef = useRef(null);
  const peBodyTableRef = useRef(null);
  const ceHeadClipRef = useRef(null);
  const peHeadClipRef = useRef(null);
  const ceHeadTableRef = useRef(null);
  const hScrollRef = useRef(null);
  const hScrollInnerRef = useRef(null);
  const rafRef = useRef(null);
  const maxOffsetRef = useRef(0);
  const initDoneRef = useRef(false);
  const stickyBadgeRef = useRef(null);
  const atmBadgeRef = useRef(null);
  const lastStickyState = useRef("visible");
  const hoverTimeoutRef = useRef(null);

  // ── Hover state ───────────────────────────────────────────────────────
  const [hoveredRow, setHoveredRow] = useState(null);
  const [hoveredSide, setHoveredSide] = useState(null);
  
  // ── Mobile Drawer State ───────────────────────────────────────────────
  const [mobileDrawerState, setMobileDrawerState] = useState({ open: false, rowData: null, side: null });

  const handleRowMouseEnter = useCallback((ri, side) => {
    if (isMobile) return;
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setHoveredRow(ri);
    if (side) setHoveredSide(side);
  }, [isMobile]);

  const handleRowMouseLeave = useCallback(() => {
    if (isMobile) return;
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredRow(null);
      setHoveredSide(null);
    }, 150);
  }, [isMobile]);


  // Close active mobile row if user clicks outside the table
  useEffect(() => {
    if (!isMobile) return;
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setHoveredRow(null);
        setHoveredSide(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isMobile]);

  // ── Data hook (WebSockets + dynamic rows + spot price) ────────────────
  const { dynamicRows, spotPrice } = useOptionChainData({
    arrRows, exchange, underlyingToken, underlyingLtp
  });

  // ── Scroll state ──────────────────────────────────────────────────────
  const [initialScrollDone, setInitialScrollDone] = useState(false);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);

  // ── Column filtering ──────────────────────────────────────────────────
  const isHeaderSelected = useCallback((header) => {
    if (!selectedColumns || !Array.isArray(selectedColumns)) return true;
    return selectedColumns.includes(header.toLowerCase());
  }, [selectedColumns]);

  const filteredCallHeaders = callHeaders.filter(isHeaderSelected);
  const filteredPutHeaders = putHeaders.filter(isHeaderSelected);

  // ── Chart modal state ─────────────────────────────────────────────────
  const [switchViews, setSwitchViews] = useState(false);
  const [chartQuery, setChartQuery] = useState('');

  // ── Derived rows / ATM index ──────────────────────────────────────────
  const filteredRows = React.useMemo(() => dynamicRows || [], [dynamicRows]);

  const handleRowClick = useCallback((ri, side) => {
    if (!isMobile) return;
    const row = filteredRows[ri];
    if (!row) return;

    // Build the instrument data from the row
    const instrumentData = side === 'ce' ? row.ce_data : row.pe_data;
    const tokenVal = instrumentData?.token || row[`${side}_token`] || '';
    
    const rowData = {
      ltp: side === 'ce' ? row.ce_ltp : row.pe_ltp,
      trading_symbol: instrumentData?.trading_symbol || row[`${side}_trading_symbol`] || '',
      display_name: instrumentData?.display_name || row[`${side}_display_name`] || '',
      token: tokenVal,
      exchange: instrumentData?.exchange || row[`${side}_exchange`] || 'NSE',
      tradable: instrumentData?.tradable !== false && row[`${side}_tradable`] !== false
    };

    setMobileDrawerState({ open: true, rowData, side });
  }, [isMobile, filteredRows]);

  const atmIndex = React.useMemo(() => {
    if (!spotPrice || !filteredRows || filteredRows.length === 0) return -1;
    let left = 0, right = filteredRows.length - 1;
    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      const curr = parseFloat(filteredRows[mid]?.strike_price || 0);
      const next = parseFloat(filteredRows[mid + 1]?.strike_price || Infinity);
      if (curr <= spotPrice && spotPrice < next) return mid;
      if (spotPrice < curr) right = mid - 1;
      else left = mid + 1;
    }
    return -1;
  }, [spotPrice, filteredRows]);

  // ── Reset scroll when strikes count changes ───────────────────────────
  useEffect(() => {
    setInitialScrollDone(false);
    setInitialDataLoaded(false);
  }, [arrRows.length]);

  useEffect(() => {
    if (dynamicRows?.length > 0 && spotPrice > 0 && !initialDataLoaded && !initialScrollDone) {
      setTimeout(() => setInitialDataLoaded(true), 200);
    }
  }, [dynamicRows, spotPrice, initialDataLoaded, initialScrollDone]);

  // ── Horizontal scroll sync ────────────────────────────────────────────
  const applyOffset = useCallback((v, source) => {
    const M = maxOffsetRef.current;
    const ceL = Math.round(M - v);
    const peL = Math.round(v);

    if (source !== "ce") {
      if (ceBodyClipRef.current && ceBodyClipRef.current.scrollLeft !== ceL) ceBodyClipRef.current.scrollLeft = ceL;
      if (ceHeadClipRef.current && ceHeadClipRef.current.scrollLeft !== ceL) ceHeadClipRef.current.scrollLeft = ceL;
    }
    if (source !== "pe") {
      if (peBodyClipRef.current && peBodyClipRef.current.scrollLeft !== peL) peBodyClipRef.current.scrollLeft = peL;
      if (peHeadClipRef.current && peHeadClipRef.current.scrollLeft !== peL) peHeadClipRef.current.scrollLeft = peL;
    }
    if (source !== "h") {
      if (hScrollRef.current && hScrollRef.current.scrollLeft !== v) hScrollRef.current.scrollLeft = v;
    }
  }, []);

  // Prevent Mac swipe-back navigation on horizontal wheel
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const handleWheel = (e) => {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        if (hScrollRef.current) {
          e.preventDefault();
          hScrollRef.current.scrollLeft += e.deltaX;
        }
      }
    };
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, []);

  const onHScroll = useCallback(() => {
    applyOffset(hScrollRef.current?.scrollLeft ?? 0, "h");
  }, [applyOffset]);

  const onCEScroll = useCallback((e) => {
    const v = Math.max(0, Math.min(maxOffsetRef.current, maxOffsetRef.current - e.target.scrollLeft));
    applyOffset(v, "ce");
  }, [applyOffset]);

  const onPEScroll = useCallback((e) => {
    const v = Math.max(0, Math.min(maxOffsetRef.current, e.target.scrollLeft));
    applyOffset(v, "pe");
  }, [applyOffset]);

  // Attach native scroll listeners for 0ms lag
  useEffect(() => {
    const ceBody = ceBodyClipRef.current;
    const peBody = peBodyClipRef.current;
    const hs = hScrollRef.current;

    if (ceBody) ceBody.addEventListener('scroll', onCEScroll, { passive: true });
    if (peBody) peBody.addEventListener('scroll', onPEScroll, { passive: true });
    if (hs) hs.addEventListener('scroll', onHScroll, { passive: true });

    return () => {
      if (ceBody) ceBody.removeEventListener('scroll', onCEScroll);
      if (peBody) peBody.removeEventListener('scroll', onPEScroll);
      if (hs) hs.removeEventListener('scroll', onHScroll);
    };
  }, [onCEScroll, onPEScroll, onHScroll]);

  // ── Master scrollbar width sync ───────────────────────────────────────
  const setupScroll = useCallback(() => {
    const ceTable = ceBodyTableRef.current;
    const ceClip = ceBodyClipRef.current;
    const hScroll = hScrollRef.current;
    const inner = hScrollInnerRef.current;
    if (!ceTable || !ceClip || !hScroll || !inner) return;

    const peHeadTable = peHeadClipRef.current?.querySelector("table");
    const ceW = ceClip.scrollWidth;
    const clipW = ceClip.clientWidth;
    const M = Math.max(0, ceW - clipW);
    maxOffsetRef.current = M;

    if (ceHeadTableRef.current) {
      ceHeadTableRef.current.style.width = ceTable.style.width || ceTable.offsetWidth + 'px';
      ceHeadTableRef.current.style.minWidth = ceTable.style.minWidth || '';
    }
    if (peHeadTable && peBodyClipRef.current?.querySelector("table")) {
      const peBody = peBodyClipRef.current.querySelector("table");
      peHeadTable.style.width = peBody.style.width || peBody.offsetWidth + 'px';
      peHeadTable.style.minWidth = peBody.style.minWidth || '';
    }

    if (M > 0) {
      hScroll.style.display = 'block';
      inner.style.width = (M + hScroll.clientWidth) + "px";
    } else {
      hScroll.style.display = 'none';
      inner.style.width = "100%";
    }

    if (!initDoneRef.current) {
      initDoneRef.current = true;
      hScroll.scrollLeft = 0;
      applyOffset(0, "h");
    }
  }, [applyOffset]);

  useEffect(() => {
    initDoneRef.current = false;
    setupScroll();
  }, [filteredRows.length, filteredCallHeaders.length, filteredPutHeaders.length, isMobile, setupScroll]);

  useEffect(() => {
    if (!outerRef.current) return;
    const observer = new ResizeObserver(() => {
      initDoneRef.current = false;
      setupScroll();
    });
    observer.observe(outerRef.current);
    const timer = setTimeout(setupScroll, 200);
    return () => { observer.disconnect(); clearTimeout(timer); };
  }, [setupScroll]);

  // ── ATM centering ─────────────────────────────────────────────────────
  const scrollToATM = useCallback(() => {
    if (!outerRef.current) return;
    const strikeBody = outerRef.current.querySelector('.oc-strike-body');
    if (!strikeBody) return;
    const allRows = strikeBody.querySelectorAll('tr');
    if (!allRows.length || atmIndex == null) return;
    const atmRow = allRows[atmIndex];
    if (!atmRow) return;
    const containerHeight = outerRef.current.clientHeight;
    const scrollPosition = atmRow.offsetTop - (containerHeight / 2) + (atmRow.clientHeight / 2);
    outerRef.current.scrollTo({ top: Math.max(0, scrollPosition), behavior: 'smooth' });
  }, [atmIndex]);

  useEffect(() => {
    if (atmIndex !== undefined && atmIndex !== null && atmIndex !== -1 &&
        filteredRows?.length > 0 && spotPrice > 0 && !initialScrollDone && outerRef.current) {
      const timer = setTimeout(() => {
        requestAnimationFrame(() => {
          scrollToATM();
          setInitialScrollDone(true);
        });
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [atmIndex, filteredRows, spotPrice, initialScrollDone, scrollToATM]);

  // ── Sticky ATM badge ──────────────────────────────────────────────────
  useEffect(() => {
    const container = outerRef.current;
    const stickyBadge = stickyBadgeRef.current;
    if (!container || !stickyBadge) return;

    const update = () => {
      const strikeBody = container.querySelector('.oc-strike-body');
      if (!strikeBody) { stickyBadge.style.display = "none"; return; }
      const atmRow = strikeBody.querySelector(".oc-atm-row");
      const realBadge = atmBadgeRef.current;
      if (!atmRow) { stickyBadge.style.display = "none"; return; }

      const cRect = container.getBoundingClientRect();
      const cBot = cRect.top + container.clientHeight;
      const atmY = atmRow.getBoundingClientRect().top;

      const newState = atmY <= cRect.top ? "top" : atmY >= cBot ? "bottom" : "visible";
      if (newState !== lastStickyState.current) {
        lastStickyState.current = newState;
        if (newState === "visible") {
          stickyBadge.style.display = "none";
          if (realBadge) realBadge.style.visibility = "visible";
        } else {
          stickyBadge.style.display = "flex";
          if (realBadge) realBadge.style.visibility = "hidden";
          const arrow = stickyBadge.querySelector(".oc-sticky-badge__arrow");
          if (arrow) arrow.textContent = newState === "top" ? "↑" : "↓";
        }
      }

      if (stickyBadge.style.display === "flex") {
        const inner = stickyBadge.querySelector('.oc-sticky-badge__inner');
        if (lastStickyState.current === "top") {
          const theadHeight = container.parentElement.querySelector('.oc-header')?.offsetHeight || 38;
          stickyBadge.style.top = theadHeight + "px";
          stickyBadge.style.bottom = "auto";
          if (inner) inner.style.transform = "translate(-50%, 0)";
        } else {
          stickyBadge.style.bottom = "12px";
          stickyBadge.style.top = "auto";
          if (inner) inner.style.transform = "translate(-50%, -100%)";
        }
        stickyBadge.style.left = strikeBody.offsetLeft + "px";
        stickyBadge.style.width = strikeBody.offsetWidth + "px";
      }
    };

    const onScroll = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => { update(); rafRef.current = null; });
    };

    container.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    const t = setTimeout(update, 400);
    return () => {
      container.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      clearTimeout(t);
    };
  }, []);

  // ── LTP click → open order window ────────────────────────────────────
  const handleLTPClick = (row, side, event, orderType = 'BUY') => {
    if (event) { event.preventDefault(); event.stopPropagation(); }

    const instrumentData = side === 'ce' ? row.ce_data : row.pe_data;
    const tokenVal = instrumentData?.token || row[`${side}_token`] || '';

    if (!instrumentData?.trading_symbol || !instrumentData?.exchange || !tokenVal) {
      enqueueSnackbar("Invalid instrument data", { variant: "error" });
      return;
    }

    setTimeout(() => {
      dispatch(openOrderWindow({
        type: orderType,
        trading_symbol: instrumentData.trading_symbol,
        display_name: instrumentData.display_name || instrumentData.trading_symbol,
        ltp: side === 'ce' ? row.ce_ltp : row.pe_ltp,
        exchange: instrumentData.exchange,
        token: tokenVal,
        product: getProductType(userSettings.product_type, instrumentData.exchange),
        order_type: userSettings.order_type
      }));
    }, 100);
  };

  // ── Column width calculations ─────────────────────────────────────────
  const strikeW = '90px';
  const colMinPct = isMobile ? 55 : 20;
  const callTableWidth = `${Math.max(100, filteredCallHeaders.length * colMinPct)}%`;
  const putTableWidth = `${Math.max(100, filteredPutHeaders.length * colMinPct)}%`;
  const ceColWidthPct = filteredCallHeaders.length > 0 ? `${100 / filteredCallHeaders.length}%` : '100%';
  const peColWidthPct = filteredPutHeaders.length > 0 ? `${100 / filteredPutHeaders.length}%` : '100%';

  const ceColgroup = <colgroup>{filteredCallHeaders.map((_, i) => <col key={i} />)}</colgroup>;
  const peColgroup = <colgroup>{filteredPutHeaders.map((_, i) => <col key={i} />)}</colgroup>;
  const strikeColgroup = <colgroup><col style={{ width: strikeW, minWidth: strikeW, maxWidth: strikeW }} /></colgroup>;

  // ── Shared props for header and body ──────────────────────────────────
  const sharedTableProps = {
    filteredCallHeaders, filteredPutHeaders,
    callTableWidth, putTableWidth, strikeW,
    ceColWidthPct, peColWidthPct,
    ceColgroup, peColgroup, strikeColgroup,
  };

  return (
    <TableWrapper ref={wrapperRef}>
      <OptionChainTableHeader
        {...sharedTableProps}
        ref={{ ceHeadClipRef, peHeadClipRef, ceHeadTableRef }}
      />

      {/* Vertical scroll wrapper — scrollbar only covers body */}
      <div ref={outerRef} className="oc-outer">
        <OptionChainTableBody
          {...sharedTableProps}
          isMobile={isMobile}
          filteredRows={filteredRows}
          dynamicRows={dynamicRows}
          atmIndex={atmIndex}
          spotPrice={spotPrice}
          hoveredRow={hoveredRow}
          hoveredSide={hoveredSide}
          handleRowMouseEnter={handleRowMouseEnter}
          handleRowMouseLeave={handleRowMouseLeave}
          handleRowClick={handleRowClick}
          handleLTPClick={handleLTPClick}
          atmBadgeRef={atmBadgeRef}
          scrollToATM={scrollToATM}
          ref={{ ceBodyClipRef, peBodyClipRef, ceBodyTableRef, peBodyTableRef }}
        />
      </div>

      {/* Mobile Actions Drawer */}
      <MobileOptionDrawer
        open={mobileDrawerState.open}
        onClose={() => setMobileDrawerState({ ...mobileDrawerState, open: false })}
        instrument={mobileDrawerState.rowData}
        onBuyClick={() => handleLTPClick({ [`${mobileDrawerState.side}_data`]: mobileDrawerState.rowData, [`${mobileDrawerState.side}_ltp`]: mobileDrawerState.rowData?.ltp }, mobileDrawerState.side, null, 'BUY')}
        onSellClick={() => handleLTPClick({ [`${mobileDrawerState.side}_data`]: mobileDrawerState.rowData, [`${mobileDrawerState.side}_ltp`]: mobileDrawerState.rowData?.ltp }, mobileDrawerState.side, null, 'SELL')}
        onChartClick={() => {
          if (mobileDrawerState.rowData) {
            navigate(`/charts?exchange=${mobileDrawerState.rowData.exchange}&token=${mobileDrawerState.rowData.token}`);
          }
        }}
      />

      {/* Sticky ATM Badge */}
      <div ref={stickyBadgeRef} className="oc-sticky-badge" onClick={(e) => { e.stopPropagation(); scrollToATM(); }}>
        <div className="oc-sticky-badge__inner">
          <span className="oc-sticky-badge__arrow">↑</span>
          {spotPrice ? spotPrice.toFixed(2) : "0.00"}
        </div>
      </div>

      {/* Master Scrollbar */}
      <div ref={hScrollRef} className="oc-hscroll" onScroll={onHScroll}>
        <div ref={hScrollInnerRef} className="oc-hscroll-inner" />
      </div>

      <ChartFrameModal
        switchViews={switchViews}
        setSwitchViews={setSwitchViews}
        url={chartQuery}
        defaultTab={'chart'}
      />
    </TableWrapper>
  );
});

export default OptionTbl;
